import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  GetProductionZonesListProps,
  getProductionZonesList,
} from "@/services/production-zones.service";
import { handlePermissionError } from "@/utils/permission-check";
import { FieldValues, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { ProductionZonesListResponse } from "./production-zones.schema";
import { getOptionalBooleanValue } from "@/utils/request-validations";

interface FetchProductionZonesHandlerProps {
  setLoadingProductionZones: Dispatch<SetStateAction<boolean>>;
  setProductionZones: Dispatch<SetStateAction<ProductionZonesListResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
  filters?: Partial<GetProductionZonesListProps>;
}


const getZoneValues = (value: unknown) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value.trim() ? [value.trim()] : [];
  }

  return [] as string[];
};


const getProductionZonesFilter = (
  event: FieldValues,
): Partial<GetProductionZonesListProps> => {
  const filterObj: Partial<GetProductionZonesListProps> = {};

  if (event?.Nombre?.trim()) filterObj.Nombre = event.Nombre.trim();

  const zonaComercial = getZoneValues(event?.ZonaComercial);
  if (zonaComercial.length) filterObj.ZonaComercial = zonaComercial;

  const activo = getOptionalBooleanValue(event?.Activo);
  if (typeof activo === "boolean") filterObj.Activo = activo;

  return filterObj;
};

export const fetchProductionZonesHandler = async ({
  setLoadingProductionZones,
  setProductionZones,
  navigate,
  fetchPermissions,
  showLoad = true,
  filters,
}: FetchProductionZonesHandlerProps) => {
  try {
    if (showLoad) setLoadingProductionZones(true);

    const request: GetProductionZonesListProps = {
      "P.Pagina": (filters as any)?.["P.Pagina"] ?? 1,
      "P.Records": (filters as any)?.["P.Records"] ?? 10,
    };

    if (filters?.Nombre) request.Nombre = filters.Nombre;
    if (filters?.ZonaComercial?.length) {
      request.ZonaComercial = filters.ZonaComercial;
    }
    if (typeof filters?.Activo === "boolean") request.Activo = filters.Activo;

    const response = await getProductionZonesList(request);
    setProductionZones(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.PRODUCTION_ZONES,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el listado de zonas productivas.",
      });
    }
  } finally {
    if (showLoad) setLoadingProductionZones(false);
  }
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<Partial<GetProductionZonesListProps> | undefined>>;
  setPage: Dispatch<SetStateAction<number>>;
  fetchProductionZones: (
    filters?: Partial<GetProductionZonesListProps>,
    showLoad?: boolean,
  ) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setOpenFilters,
  setFiltersApplied,
  setCurrentFilters,
  setPage,
  fetchProductionZones,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj = getProductionZonesFilter(event);

  setCurrentFilters(filterObj);
  if (!Object.keys(filterObj).length) setFiltersApplied(false);

  setPage(1);
  await fetchProductionZones({ ...filterObj, "P.Pagina": 1 }, true);
  setOpenFilters(false);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  submitFilters: (event?: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = async ({
  setValue,
  submitFilters,
  getValues,
  setFiltersApplied,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("Nombre", "");
  setValue("ZonaComercial", []);
  setValue("Activo", []);
  await submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};
